import React, { useEffect } from 'react'
import Helmet from 'react-helmet'
import queryString from 'query-string'
import { Location } from 'history'

// Constants
import { RouteProps, routes, SKETCH_WEBSITE } from '@sketch/constants'

// Utils
import { useQueryParams } from 'utils'
import { useGoogleAnalytics } from 'modules/analytics/google-analytics'

// Hooks
import { getIsForInAppPresentation } from 'modules/user/hooks/useSketchSource'

// Components
import SignInForm from 'components/SignInForm'
import IndexLayout, { Title } from 'components/IndexLayout'
import RedirectBadge from 'components/RedirectBadge'
import OrderedBreadcrumbs from 'components/OrderedBreadcrumbs'
import { useModalContext } from 'containers/ModalManager/ModalContext'
import SketchLoginDeprecationModal from '../../modals/SketchLoginDeprecationModal'
import { Link, DocumentHead } from '@sketch/components'

// Styles
import {
  BreadcrumbsWrapper,
  BottomSection,
  Line,
  StyledLinkButton,
  FormContainer,
  Paragraph,
} from './SignInView.styles'

const SIGN_IN_CRUMBS = [{ content: 'Sign In' }, { content: 'Create Workspace' }]
interface LocationState {
  from: Location
}
interface SignInViewProps extends RouteProps<'SIGN_IN'> {
  location: Location<LocationState>
}

const SignInView: React.FC<SignInViewProps> = ({ history, location }) => {
  const { sketch } = useQueryParams<'SIGN_IN'>()
  const { trackGAEvent } = useGoogleAnalytics()

  const isForInAppPresentation = getIsForInAppPresentation()

  const { state, pathname } = location
  const linkState = { from: state?.from }

  useEffect(() => {
    trackGAEvent('event', 'page_view', {
      page_path: pathname,
    })
  }, [trackGAEvent, pathname])

  const { email }: { email?: string } = queryString.parse(state?.from?.search)

  const { showModal } = useModalContext()

  const target = isForInAppPresentation ? '_self' : '_blank'
  const willContinueToWorkspaceCreation =
    state?.from?.pathname === routes.WORKSPACE_CREATE.create({})

  return (
    <>
      <Helmet>
        <link rel="canonical" href="https://www.sketch.com/signin/" />
        <meta property="og:url" content="https://www.sketch.com/signin/" />
      </Helmet>
      <DocumentHead title="Sign in - It's great to see you again" />
      <IndexLayout
        headerLink="none-with-border"
        variant="orange"
        blockLogoLink={isForInAppPresentation}
      >
        {state?.from && <RedirectBadge {...state} />}

        {willContinueToWorkspaceCreation && (
          <BreadcrumbsWrapper>
            <OrderedBreadcrumbs crumbs={SIGN_IN_CRUMBS} currentCrumb={0} />
          </BreadcrumbsWrapper>
        )}

        <Title>Sign in to Sketch</Title>
        <FormContainer>
          <SignInForm
            email={email}
            onCompleted={data => {
              // MFA auth
              if (data.signIn.__typename === 'MFACredentials') {
                history.push(routes.VERIFICATION_CODE.create({}), {
                  mfaToken: data.signIn.mfaToken,
                  from: state?.from,
                })
                return
              }

              // U+P auth
              if (sketch === 'true') {
                showModal(SketchLoginDeprecationModal)
              }

              // Partner invitation URL contains the "partner" parameter. This
              // allow us to go directly to the subscribe page without passing
              // on AcceptWorkspaceInviteView and be redirected from there.
              const destinationRouteParams = new URLSearchParams(
                state?.from.search
              )
              const isPartner = destinationRouteParams.get('partner')
              const token = destinationRouteParams.get('token')
              if (isPartner && token) {
                history.push(
                  routes.WORKSPACE_SUBSCRIBE_PARTNER.create({
                    query: {
                      token,
                    },
                  })
                )
                return
              }

              if (willContinueToWorkspaceCreation) {
                history.push(routes.WORKSPACE_CREATE.create({}), {
                  fromSignIn: true,
                })
              } else {
                history.push(state?.from || routes.ENTRY.create({}))
              }
            }}
          />
          <StyledLinkButton
            size="40"
            variant="secondary"
            to={{
              pathname: routes.SSO_SIGN_IN.create({}),
              state: linkState,
            }}
          >
            Sign in with SSO
          </StyledLinkButton>
          <Line />
          <BottomSection>
            {!isForInAppPresentation && (
              <Paragraph>
                New to Sketch?{' '}
                <Link
                  variant="primary"
                  isUnderlined
                  to={{
                    pathname: routes.SIGN_UP.create({}),
                    state,
                  }}
                >
                  Create an account
                </Link>
              </Paragraph>
            )}
            <Paragraph>
              Using Sketch with a License?{' '}
              <Link
                variant="primary"
                isUnderlined
                target={target}
                href={`${SKETCH_WEBSITE}/support/license-manager`}
                external
              >
                Learn more
              </Link>
            </Paragraph>
          </BottomSection>
        </FormContainer>
      </IndexLayout>
    </>
  )
}

export default SignInView
